/**
 * Workflow API client — typed access to the live Scrips.Workflows REST surface
 * (the Elsa 2.5.0 service, .NET 6).
 *
 * Grounded in /Users/samertadross/scrips-repos/Scrips.Workflows-readonly AND
 * confirmed live against the deployed engine: a GET to
 * `https://dev-workflows.scrips.com/v1/workflow-definitions` returns 200 with
 * an `{ items, totalCount }` body — so the Elsa management-API routes and the
 * page envelope below are VERIFIED, not assumed.
 *
 * Two distinct API surfaces live in that service:
 *
 *   1. CUSTOM CONTROLLERS (verified — API/Controllers/Workflows/*.cs). These
 *      RUN workflows and READ activity state. All are unauthenticated HttpGet
 *      and wrap their payload in the .NET `ResponseModel<T>` envelope
 *      ({ correlationId, message, params, data, total }) — see
 *      Infrastructure/Response/ResponseModel.cs.
 *
 *   2. ELSA MANAGEMENT API (Program.cs:53 `AddElsaApiEndpoints()`). The
 *      Designer console uses these to list / get / save / publish / delete
 *      workflow DEFINITIONS. The base collection route
 *      `GET /v1/workflow-definitions` and its `{ items, totalCount }` envelope
 *      are CONFIRMED-live (curled — 200), as are the per-item sub-routes
 *      (`/{id}/{versionOptions}`, `/{id}/publish`, `/{id}/retract`, and DELETE
 *      `/{id}`), each re-checked against a live engine on 2026-09-11 rather
 *      than inferred from REST convention. Segment count is load-bearing here:
 *      see {@link getWorkflowDefinition} for the one-vs-two-segment collision
 *      that made every by-id read 404 (PROD-2134).
 *
 * ── Reachability ──────────────────────────────────────────────────────────
 * Browser → engine is CORS/auth-gated until Andrew's B deploy lands. This
 * client is wired code-complete and base-URL-configurable; it activates on
 * deploy. It NEVER returns a silent mock — an unreachable engine (fetch
 * reject) or a non-2xx response throws {@link ApiError}, surfaced to the
 * caller.
 *
 * ── Host ──────────────────────────────────────────────────────────────────
 * The Scrips.Workflows engine is a DIFFERENT origin from the main PM API, so
 * this module does NOT route through `@/shared/api/api-client` (which hardcodes
 * `VITE_API_BASE_URL` — the wrong host — and attaches tenant/auth headers the
 * unauthenticated engine neither needs nor expects, and which would trip CORS
 * preflight cross-origin). It owns a thin fetch wrapper targeting a CONFIGURABLE
 * base URL (`VITE_WORKFLOW_ENGINE_URL` → dev-workflows.scrips.com), reusing the
 * shared {@link ApiError} type and the same non-2xx → throw contract so the
 * global query error handler treats a workflow-engine failure like any other.
 * Mirrors the sibling `rule-api.ts` engine client.
 */

import { ApiError } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';

// ---------------------------------------------------------------------------
// Response envelope — Infrastructure/Response/ResponseModel.cs (VERIFIED)
// ---------------------------------------------------------------------------

/**
 * The .NET `ResponseModel<T>` wrapper returned by the custom workflow
 * controllers. Field names are camelCased on the wire by the default
 * System.Text.Json policy (the C# properties are PascalCase: CorrelationId,
 * Message, Params, Data, Total).
 */
export interface ResponseModel<T> {
  correlationId: string;
  message: string;
  params: Array<{ key: string; value: string }>;
  data: T;
  /** Virtual total count of the entity (1 for single, N for collections). */
  total: number;
}

// ---------------------------------------------------------------------------
// Workflow DEFINITION shapes
// ---------------------------------------------------------------------------

/**
 * DB row wrapper for a stored workflow definition — mirrors
 * Application/Resources/Workflow/WorkflowDefinitionResource.cs (VERIFIED),
 * which the custom code reads from the `elsa.WorkflowDefinitions` table. The
 * Elsa management API returns a JSON object that is a structural superset of
 * this (it nests the graph as `activities`/`connections` arrays rather than a
 * serialized `data` string), so the management-API fields are marked optional
 * and the graph is exposed via {@link WorkflowDefinition.data}.
 *
 * `data` holds the workflow GRAPH as a JSON string in the DB-row form, OR is
 * absent when the management API returns the graph inline (see
 * {@link WorkflowDefinition}).
 */
export interface WorkflowDefinitionResource {
  /** Surrogate row id (string GUID). */
  id: string;
  /** Stable definition id, shared across versions. */
  definitionId: string;
  tenantId: string | null;
  name: string;
  displayName: string;
  description: string | null;
  version: number;
  isSingleton: boolean;
  /** Elsa WorkflowPersistenceBehavior enum, serialized as an int. */
  persistenceBehavior: number;
  deleteCompletedInstances: boolean;
  isPublished: boolean;
  isLatest: boolean;
  tag: string | null;
  /** The workflow graph as a JSON string (the DB `Data` column). */
  data: string | null;
}

/**
 * The workflow GRAPH — Application/Resources/Workflow/Data/WorkflowData.cs
 * (VERIFIED). This is what lives inside `WorkflowDefinitionResource.data`
 * once parsed (use {@link parseWorkflowGraph}), and what the Elsa management
 * API nests inline on a definition.
 */
export interface WorkflowData {
  /** System.Text.Json reference id ($id). */
  $id?: string;
  activities: WorkflowDataActivity[];
  connections: WorkflowDataConnection[];
  variables?: WorkflowDataCustomAttributes;
  customAttributes?: WorkflowDataCustomAttributes;
}

/**
 * A single node in the graph — WorkflowDataActivity.cs (VERIFIED).
 * `type` is the activity class name (e.g. "LoadPatientActivity"); `properties`
 * is an opaque object array the FE does not need to introspect for read.
 */
export interface WorkflowDataActivity {
  $id?: string;
  /** Activity instance id (GUID). Connections reference this. */
  activityId: string;
  /** Activity class name — the discriminator. */
  type: string;
  name: string | null;
  displayName: string | null;
  persistWorkflow: boolean;
  loadWorkflowContext: boolean;
  saveWorkflowContext: boolean;
  /** Opaque property bag (object[] in source). */
  properties: unknown[];
  /** WorkflowDataDatum — an empty marker object in source. */
  propertyStorageProviders?: Record<string, never>;
}

/**
 * An edge — WorkflowDataConnection.cs (VERIFIED). `outcome` is the branch
 * label the canvas renders (e.g. "Done" | "Success" | "Failed" — Send*
 * activities emit Success/Failed, the rest default to "Done").
 */
export interface WorkflowDataConnection {
  $id?: string;
  sourceActivityId: string;
  targetActivityId: string;
  outcome: string;
}

/**
 * variables / customAttributes container — WorkflowDataCustomAttributes.cs
 * (VERIFIED). `data` is an empty marker object (WorkflowDataDatum) in source.
 */
export interface WorkflowDataCustomAttributes {
  $id?: string;
  data?: Record<string, never>;
}

/**
 * A workflow definition as returned by the Elsa management API. The Elsa 2.5
 * framework nests the graph inline (activities + connections) rather than as a
 * serialized `data` string. Fields overlap the DB-row
 * {@link WorkflowDefinitionResource}; the graph is exposed inline here. Treated
 * as a superset so a caller can read either form.
 */
export interface WorkflowDefinition
  extends Omit<WorkflowDefinitionResource, 'data'> {
  /** Inline graph (Elsa management API form). */
  activities?: WorkflowDataActivity[];
  /** Inline graph (Elsa management API form). */
  connections?: WorkflowDataConnection[];
  /**
   * Always the `{ $id?, data }` container by the time a caller sees it. The
   * engine sends EITHER shape: dev-workflows returned a JSON STRING of the flat
   * bag (no `data` wrapper) for every definition checked on 2026-10-03, while
   * Elsa's own `Variables` type serializes as `{ data }`. Every response path in
   * this client runs {@link normalizeWorkflowDefinition}, so readers keep reading
   * `def.variables?.data?.[KEY]` whichever one arrived.
   */
  variables?: WorkflowDataCustomAttributes;
  customAttributes?: WorkflowDataCustomAttributes;
}

/**
 * Read a definition's `variables` (or `customAttributes`) in either engine shape and return the object
 * container. A string is parsed as the flat bag and wrapped as `{ data }`; a
 * string that is empty, unparseable, or not a JSON object reads as an EMPTY bag
 * and never throws (a load must not die on one bad field, and the raw text is
 * not echoed anywhere). An object container passes through untouched, and so
 * do `null` and `undefined`.
 */
export function normalizeWorkflowVariables(
  variables: unknown,
): WorkflowDataCustomAttributes | null | undefined {
  if (variables === null || variables === undefined) return variables;
  if (typeof variables === 'string') {
    let parsed: unknown;
    try {
      parsed = JSON.parse(variables);
    } catch {
      parsed = undefined;
    }
    const bag =
      typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : {};
    return { data: bag as WorkflowDataCustomAttributes['data'] };
  }
  if (typeof variables === 'object' && !Array.isArray(variables)) {
    return variables as WorkflowDataCustomAttributes;
  }
  return { data: {} };
}

/**
 * Normalise one definition off the wire; see {@link normalizeWorkflowVariables}.
 * Elsa 2.5 sends BOTH `variables` and `customAttributes` as JSON strings, so both
 * go through the same normaliser.
 */
function normalizeWorkflowDefinition<
  T extends { variables?: unknown; customAttributes?: unknown },
>(def: T): T {
  if (!def || typeof def !== 'object') return def;
  let next = def;
  for (const key of ['variables', 'customAttributes'] as const) {
    if (!(key in next)) continue;
    const normalized = normalizeWorkflowVariables(next[key]);
    if (normalized !== next[key]) next = { ...next, [key]: normalized };
  }
  return next;
}

// ---------------------------------------------------------------------------
// Activity (runtime) shapes — custom controllers
// ---------------------------------------------------------------------------

/**
 * Runtime activity record — Application/Models/Workflow/ActivityResultModel.cs
 * (VERIFIED). Returned by the activities-listing and find-activity endpoints.
 * This is RUNTIME state (started/completed/by-whom), not the definition node.
 */
export interface ActivityResultModel {
  activityId: string;
  type: string;
  name: string | null;
  displayName: string | null;
  isCompleted: boolean;
  /** ISO-8601 string or null. */
  startedAt: string | null;
  /** ISO-8601 string or null. */
  completedAt: string | null;
  completedBy: string | null;
  remarks: string | null;
}

// ---------------------------------------------------------------------------
// Configurable engine host + thin fetch wrapper
// ---------------------------------------------------------------------------

/**
 * Workflow-engine base URL, resolved at CALL time (not module load) so the host
 * can be swapped in tests and a late-injected env is honoured. Vite statically
 * replaces `import.meta.env.VITE_WORKFLOW_ENGINE_URL` at build; the `?? ''`
 * keeps a relative path working when the engine is same-origin (proxied) or the
 * env is unset in dev. Production points at `https://dev-workflows.scrips.com`.
 */
export function workflowEngineBaseUrl(): string {
  return (import.meta.env.VITE_WORKFLOW_ENGINE_URL as string | undefined) ?? '';
}

/**
 * Thin engine client — honours the app's http convention against a configurable
 * host. Mirrors `rule-api.ts#engineFetch` and `@/shared/api/api-client`: JSON
 * Content-Type on writes, non-2xx throws {@link ApiError} carrying status +
 * body, transport failure throws `ApiError(0)`, `AbortSignal` cancellation.
 * Auth: attaches the user's access token as `Authorization: Bearer`. The deployed
 * Scrips.Workflows engine runs Identity-ON (dev/prod) — its Elsa management API
 * sits behind the fail-closed FallbackPolicy, so an UNAUTHENTICATED request 401s.
 * The engine validates the SAME Identity issuer the rest of the app uses
 * (`ValidateAudience=false`) and its CORS allows any header, so the app's bearer
 * authenticates AND carries the `tenant` claim the engine stamps onto definitions
 * (correct multi-tenant scoping). On a 401 we force-refresh once and retry — the
 * 401 safety net mirrors `@/shared/api/api-client` (revoked token / clock skew).
 * A reachable engine that returns non-2xx still surfaces as a visible error —
 * NEVER a silent mock. (Local dev with an Identity-OFF engine still works: the
 * token is simply ignored by an engine with no auth scheme.)
 *
 * Exported so every Scrips.Workflows route this app calls goes through ONE
 * client and ONE base URL — `care-model-enrolments-api.ts` uses it for the
 * care-model enrolment routes rather than growing a second host setting.
 */
export async function engineFetch<T>(
  path: string,
  init: { method: 'GET' | 'POST' | 'DELETE'; body?: unknown; signal?: AbortSignal },
): Promise<T> {
  const base = workflowEngineBaseUrl();
  // Join base + path with exactly one slash, tolerating a trailing slash on the
  // base and a leading slash on the path.
  const url = base
    ? `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`
    : `/${path.replace(/^\//, '')}`;

  const { getValidAccessToken, forceRefresh } = useAuthStore.getState();

  const send = (token: string): Promise<Response> => {
    const headers = new Headers();
    if (init.body !== undefined) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(url, {
      method: init.method,
      headers,
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      ...(init.signal ? { signal: init.signal } : {}),
    });
  };

  let res: Response;
  try {
    res = await send(await getValidAccessToken());
    // 401 safety net: revoked token / clock skew — retry once with the token
    // forceRefresh resolves (mirrors @/shared/api/api-client; don't re-resolve).
    if (res.status === 401) {
      res = await send(await forceRefresh());
    }
  } catch (err) {
    // Transport failure (engine down / DNS / CORS) — fetch rejects with no HTTP
    // status. Re-throw as ApiError(0) so callers handle it uniformly with a
    // non-2xx, and so it is NEVER mistaken for a successful/empty result.
    const detail = err instanceof Error ? err.message : String(err);
    throw new ApiError(0, `Workflow engine unreachable: ${detail}`);
  }

  if (!res.ok) throw new ApiError(res.status, await res.text());

  // An empty 2xx body (e.g. DELETE) → undefined. Otherwise JSON-decode.
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

/**
 * Elsa management API base. CONFIRMED-live: `GET /v1/workflow-definitions`
 * returns 200 `{ items, totalCount }` from dev-workflows.scrips.com. Mounted by
 * `AddElsaApiEndpoints()` (Program.cs:53).
 */
const ELSA_V1 = '/v1/workflow-definitions';

/** Custom-controller base (verified — `workflows/...` routes, no leading /api). */
const WORKFLOWS = '/workflows';

// ---------------------------------------------------------------------------
// DEFINITION management — Elsa management API
// ---------------------------------------------------------------------------

/**
 * List workflow definitions.
 *
 * CONFIRMED-live `GET /v1/workflow-definitions` — returns 200 `{ items,
 * totalCount }` from dev-workflows.scrips.com. The framework paginates; we type
 * the items and the `totalCount` total.
 */
export interface WorkflowDefinitionPage {
  items: WorkflowDefinition[];
  /** Elsa pagination total (CONFIRMED present in the live response body). */
  totalCount?: number;
}

export function listWorkflowDefinitions(opts: {
  signal?: AbortSignal;
  /** Which version of each definition the list answers (Elsa's default is the latest). The enrolment pickers ask for
   *  `Published` (PROD-3104): a newer draft must not hide a published care model. */
  version?: WorkflowVersionOptions;
} = {}): Promise<WorkflowDefinitionPage> {
  return engineFetch<WorkflowDefinitionPage>(opts.version ? `${ELSA_V1}?version=${opts.version}` : ELSA_V1, {
    method: 'GET',
    ...(opts.signal ? { signal: opts.signal } : {}),
  }).then((page) =>
    page && Array.isArray(page.items)
      ? { ...page, items: page.items.map(normalizeWorkflowDefinition) }
      : page,
  );
}

/**
 * Version selector for a by-id definition read. Elsa binds this route segment
 * to its own `VersionOptions` struct, so the spelling is the framework's.
 */
export type WorkflowVersionOptions =
  | 'Latest'
  | 'Published'
  | 'Draft'
  | 'LatestOrPublished'
  | 'AllVersions';

/**
 * Get a single workflow definition by its stable definitionId.
 *
 * `GET /v1/workflow-definitions/{definitionId}/{versionOptions}` — Elsa 2.5's
 * `GetByDefinitionAndVersion`. The trailing version segment is not decoration:
 * it is what selects the endpoint.
 *
 * ── Why two segments and not one (PROD-2134) ─────────────────────────
 * Elsa mounts TWO controllers under this collection, told apart only by how
 * many segments follow it:
 *
 *   /v1/workflow-definitions/{versionId}                   → GetByVersionId
 *   /v1/workflow-definitions/{definitionId}/{versionOpts}  → GetByDefinitionAndVersion
 *
 * `GetByVersionId` matches the ROW PRIMARY KEY (`WorkflowDefinition.Id`, a
 * per-version GUID) — not `definitionId`. This client called the one-segment
 * form with a definitionId, so it matched nothing and 404d for EVERY pathway,
 * every time, including the seeded built-ins. Measured against dev-workflows
 * on 2026-09-11 with a real bearer:
 *
 *   GET /v1/workflow-definitions/cardiometabolic         → 404
 *   GET /v1/workflow-definitions/cardiometabolic/Latest  → 200
 *
 * That 404 is the whole of "my care model saved and came back empty". The save
 * was never the broken half: the same live check proved POST upserts on
 * `workflowDefinitionId` (201 then 200, one row, same `versionId`, the name
 * changed in place). It was the read that could not find what the write stored.
 *
 * `Latest` is the default because the studio edits the working copy. Elsa keeps
 * `IsLatest` on the newest row whether or not it is published, so a pathway
 * loads the same before and after a publish.
 */
export function getWorkflowDefinition(
  definitionId: string,
  opts: { signal?: AbortSignal; versionOptions?: WorkflowVersionOptions } = {},
): Promise<WorkflowDefinition> {
  const version = opts.versionOptions ?? 'Latest';
  return engineFetch<WorkflowDefinition>(
    `${ELSA_V1}/${encodeURIComponent(definitionId)}/${version}`,
    {
      method: 'GET',
      ...(opts.signal ? { signal: opts.signal } : {}),
    },
  ).then(normalizeWorkflowDefinition);
}

/**
 * Payload for create/update of a definition. The graph travels inline as
 * `activities` + `connections` (Elsa management-API form). `definitionId`
 * omitted = create; present = update that definition.
 */
export interface SaveWorkflowDefinitionRequest {
  /**
   * The definition to update; omit to create. The wire name is
   * `workflowDefinitionId`, NOT `definitionId` — the save record declares
   * `public string? WorkflowDefinitionId`, and a `definitionId` key binds to
   * nothing, silently creating a new definition on every save.
   */
  workflowDefinitionId?: string;
  name: string;
  displayName?: string;
  description?: string;
  tag?: string;
  isSingleton?: boolean;
  deleteCompletedInstances?: boolean;
  activities: WorkflowDataActivity[];
  connections: WorkflowDataConnection[];
  /**
   * A JSON STRING holding a FLAT `Record<string, unknown>` — not an object,
   * and not the `{ $id, data }` container the GET response uses.
   *
   * Elsa's request and response types for `variables` are different shapes,
   * and that asymmetry is the whole trap. On the way out the save record
   * declares `public string? Variables`, parsed by
   * `ParseVariables(json) => Deserialize<Dictionary<string, object>>(json)`.
   * On the way back `Elsa.Models.Variables` serializes as `{ data: {...} }` —
   * though dev-workflows was measured (2026-10-03) returning this same flat-bag
   * STRING instead; {@link normalizeWorkflowVariables} reads both.
   * Sending the response shape here makes Newtonsoft throw at `Path
   * 'variables'`, which nulls the ENTIRE `[FromBody]` model — so the 400
   * reads "The request field is required" (the parameter is named `request`)
   * and names `variables` only as a secondary parse error. Build it with
   * {@link encodeWorkflowVariables}.
   */
  variables?: string;
}

/**
 * Encode a flat variable bag for the save wire format — the inverse of reading
 * `WorkflowDefinition.variables.data` off a loaded definition.
 *
 * Takes the `data` bag (or the whole `{ $id, data }` container, since the
 * store's serializer emits that form) and returns the JSON string the engine
 * parses into `Dictionary<string, object>`. Returns `undefined` for an empty
 * bag so the field is omitted rather than sent as `"{}"`; the engine's
 * `ParseVariables` already treats null/whitespace as an empty `Variables`.
 */
export function encodeWorkflowVariables(
  variables:
    | { $id?: string; data?: Record<string, unknown> }
    | Record<string, unknown>
    | undefined,
): string | undefined {
  if (!variables) return undefined;
  // Unwrap the `{ $id, data }` container when given one; otherwise the value
  // IS the bag. `$id` is a serializer artefact and never a real variable.
  // Detect the read-side container STRUCTURALLY, not by the presence of a `data`
  // key: a legitimate flat bag may hold a variable literally named `data`, and
  // treating that as the wrapper would silently drop it AND every sibling
  // (`{ data: {...}, trigger: 'adt_feed' }` would encode as just the inner
  // object). The container is exactly `{ data }` or `{ $id, data }` with a
  // PLAIN-OBJECT `data` — anything else is the bag itself. Caught by Codex.
  const asContainer = variables as { $id?: string; data?: unknown };
  const keys = Object.keys(variables).filter((k) => k !== '$id');
  const isReadSideContainer =
    keys.length === 1 &&
    keys[0] === 'data' &&
    typeof asContainer.data === 'object' &&
    asContainer.data !== null &&
    !Array.isArray(asContainer.data);
  const bag = isReadSideContainer
    ? (asContainer.data as Record<string, unknown>)
    : (Object.fromEntries(
        Object.entries(variables as Record<string, unknown>).filter(
          ([k]) => k !== '$id',
        ),
      ) as Record<string, unknown>);
  return Object.keys(bag).length > 0 ? JSON.stringify(bag) : undefined;
}

/**
 * Save (create or update) a workflow definition as a DRAFT.
 *
 * `POST /v1/workflow-definitions` — the CONFIRMED-live collection route; the
 * framework upserts on `workflowDefinitionId` and bumps `version`.
 */
export function saveWorkflowDefinition(
  body: SaveWorkflowDefinitionRequest,
  opts: { signal?: AbortSignal } = {},
): Promise<WorkflowDefinition> {
  return engineFetch<WorkflowDefinition>(ELSA_V1, {
    method: 'POST',
    body,
    ...(opts.signal ? { signal: opts.signal } : {}),
  }).then(normalizeWorkflowDefinition);
}

/**
 * Publish a workflow definition (draft -> published, marks IsPublished=1 /
 * IsLatest=1 — the exact flags the custom WorkflowRepository queries on).
 *
 * `POST /v1/workflow-definitions/{definitionId}/publish` — Elsa 2.5
 * management-API REST convention.
 */
export function publishWorkflowDefinition(
  definitionId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<WorkflowDefinition> {
  // Empty body — publish is an action, not a payload.
  return engineFetch<WorkflowDefinition>(`${ELSA_V1}/${definitionId}/publish`, {
    method: 'POST',
    body: {},
    ...(opts.signal ? { signal: opts.signal } : {}),
  }).then(normalizeWorkflowDefinition);
}

/**
 * Retract (unpublish) a workflow definition (published -> draft).
 *
 * `POST /v1/workflow-definitions/{definitionId}/retract` — Elsa 2.5
 * management-API REST convention.
 */
export function retractWorkflowDefinition(
  definitionId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<WorkflowDefinition> {
  return engineFetch<WorkflowDefinition>(`${ELSA_V1}/${definitionId}/retract`, {
    method: 'POST',
    body: {},
    ...(opts.signal ? { signal: opts.signal } : {}),
  }).then(normalizeWorkflowDefinition);
}

/**
 * Delete a workflow definition (all versions).
 *
 * `DELETE /v1/workflow-definitions/{definitionId}` — Elsa 2.5 management-API
 * REST convention.
 */
export function deleteWorkflowDefinition(
  definitionId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<void> {
  return engineFetch<void>(`${ELSA_V1}/${definitionId}`, {
    method: 'DELETE',
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// ACTIVITY listing — custom controllers (VERIFIED routes + envelope)
// ---------------------------------------------------------------------------

/**
 * List the activities of the latest-published definition, by definitionId.
 *
 * VERIFIED `GET workflows/{workflowDefinitionId}/activities` —
 * FilterActivitiesByWorkflowDefinitionIdController.cs:19. Wrapped in
 * `ResponseModel<ICollection<ActivityResultModel>>`; this helper unwraps to
 * the array.
 *
 * NOTE (route collision): this path is structurally identical to
 * `workflows/{WorkflowTag}/activities`
 * (FilterActivitiesByWorkflowTagController.cs) — the two are distinguished
 * only by binding model and are ambiguous at runtime. Passing a definitionId
 * here is the intended path; tag lookups are inherently unreliable.
 */
export async function listWorkflowActivities(
  workflowDefinitionId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<ActivityResultModel[]> {
  const res = await engineFetch<ResponseModel<ActivityResultModel[]>>(
    `${WORKFLOWS}/${workflowDefinitionId}/activities`,
    { method: 'GET', ...(opts.signal ? { signal: opts.signal } : {}) },
  );
  return res.data ?? [];
}

/**
 * Find a single activity by its integer id.
 *
 * VERIFIED `GET activities/{activityId:int}` —
 * FindActivityByActivityIdController.cs:19. Wrapped in
 * `ResponseModel<ActivityResultModel>`; this helper unwraps to the record
 * (or null when not found — the controller sets Total=0 and Data=null).
 */
export async function findActivityById(
  activityId: number,
  opts: { signal?: AbortSignal } = {},
): Promise<ActivityResultModel | null> {
  const res = await engineFetch<ResponseModel<ActivityResultModel | null>>(
    `/activities/${activityId}`,
    { method: 'GET', ...(opts.signal ? { signal: opts.signal } : {}) },
  );
  return res.data ?? null;
}

// ---------------------------------------------------------------------------
// RUN endpoints — custom controllers (VERIFIED routes, all unauthenticated GET)
// ---------------------------------------------------------------------------

/**
 * Run the "Appointments" workflow for an appointment.
 *
 * VERIFIED `GET workflows/appointment/{appointmentId}` —
 * RunAppointmentWorkflowController.cs:19 (singular "appointment"). Passes the
 * workflow variable {"appointment-id": appointmentId}. Returns the
 * `ResponseModel` envelope; the run payload is not strongly typed by the repo,
 * so we return the raw envelope with an `unknown` data slot.
 */
export function runAppointmentWorkflow(
  appointmentId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<ResponseModel<unknown>> {
  return engineFetch<ResponseModel<unknown>>(
    `${WORKFLOWS}/appointment/${appointmentId}`,
    { method: 'GET', ...(opts.signal ? { signal: opts.signal } : {}) },
  );
}

/**
 * Run the "AppointmentsWorkflow" for an appointment.
 *
 * VERIFIED `GET workflows/appointments/{appointmentId}` (plural
 * "appointments") — RunAppointmentsWorkflowController.cs:19. Distinct workflow
 * from {@link runAppointmentWorkflow} despite the near-identical route.
 */
export function runAppointmentsWorkflow(
  appointmentId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<ResponseModel<unknown>> {
  return engineFetch<ResponseModel<unknown>>(
    `${WORKFLOWS}/appointments/${appointmentId}`,
    { method: 'GET', ...(opts.signal ? { signal: opts.signal } : {}) },
  );
}

/**
 * Run the "PatientTriageWorkflow" for a patient. The controller pre-loads the
 * Patient via PatientHandler and seeds variables
 * {"patient-id": patientId, "Patient": <obj>}.
 *
 * VERIFIED `GET workflows/patient-triage/{patientId}` —
 * RunPatientTriageWorkflowController.cs:22.
 */
export function runPatientTriageWorkflow(
  patientId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<ResponseModel<unknown>> {
  return engineFetch<ResponseModel<unknown>>(
    `${WORKFLOWS}/patient-triage/${patientId}`,
    { method: 'GET', ...(opts.signal ? { signal: opts.signal } : {}) },
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse the DB-row `data` JSON string into a {@link WorkflowData} graph.
 * Returns null when the string is empty/absent or fails to parse — the caller
 * decides how to surface a malformed definition. Mirrors the C# side's
 * `WorkflowData.FromJson` (WorkflowData.cs).
 */
export function parseWorkflowGraph(
  data: string | null | undefined,
): WorkflowData | null {
  if (!data) return null;
  try {
    return JSON.parse(data) as WorkflowData;
  } catch {
    return null;
  }
}
